import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs/Subscription';
import { DepositSearchResult } from '../../models/depositSearchResult.model';
import { FilterCriteria } from '../../models/filter-criteria';
import { ILimitedResult } from '../../models/limitedResult';
import { PrismDataSource } from '../../models/prism-data-source.model';
import { RemittanceEntry } from '../../models/remittance-entry.model';
import { RemittanceSummary } from '../../models/remittance-summary.model';
import { AuthorizationService } from '../../services/authorization.service';
import { GlobalFilterService } from '../../services/global-filter.service';
import { CreateRemittanceService } from '../../services/remittance-entry/create-remittance.service';
import { RemittanceEntryService } from '../../services/remittance-entry/remittance-entry.service';
import { RemittanceStageService } from '../../services/remittance-entry/remittance-stage.service';
import { RemittanceSummaryService } from '../../services/remittance-entry/remittance-summary.service';
import { CheckDetailComponent } from './check-detail/check-detail.component';
import { CreateRemittanceComponent } from './create-remittance/create-remittance.component';
import { RemittanceStageComponent } from './remittance-stage/remittance-stage.component';
import { RemittanceSummaryComponent } from './remittance-summary/remittance-summary.component';

const DEPOSIT_SEARCH_ROUTE = '/depositsearch';

//  TODO: We may or may not need to inject all these services as providers, so shake these out as needed. -JEB
//  Also, by making these providers, we're restricting the services to only be used here.
@Component({
  selector: 'app-remittance-entry',
  templateUrl: './remittance-entry.component.html',
  styleUrls: ['./remittance-entry.component.css'],
  providers: [
    RemittanceEntryService,
    RemittanceSummaryService,
    CreateRemittanceService,
    RemittanceStageService,
  ],
})
export class RemittanceEntryComponent implements OnInit, OnDestroy {
  @ViewChild('checkDetail') checkDetailComponent: CheckDetailComponent
  @ViewChild('remittanceSummary') remittanceSummaryComponent: RemittanceSummaryComponent
  @ViewChild('createRemittance') createRemittanceComponent: CreateRemittanceComponent
  @ViewChild('remittanceStage') remittanceStageComponent: RemittanceStageComponent

  private _depositSearch: DepositSearchResult = new DepositSearchResult();
  private _filterCriteria = new FilterCriteria('Check_Ref_Number', -1, 15, 0);

  public _remittanceSummary: RemittanceSummary;
  public remittanceEntryForm: FormGroup;
  private _remittanceEntries: RemittanceEntry[] = [];
  private _totalItemCount: number
  private _clientId: string
  formLoading: boolean

  //  Observables
  onCreateRemittance: Subscription
  onEditRemittance: Subscription
  onUpdateRemittance: Subscription
  onDeleteRemittance: Subscription

  //#region Properties
  get depositSearch(): DepositSearchResult {
    return this._depositSearch;
  }

  get remittanceEntries(): RemittanceEntry[] {
    return this._remittanceEntries;
  }

  get remittanceSummary(): RemittanceSummary {
    return this._remittanceSummary;
  }
//#endregion

  constructor(
    private authorizationService: AuthorizationService,
    private globalFilterService: GlobalFilterService,
    private route: ActivatedRoute,
    private router: Router,
    private remittanceEntryService: RemittanceEntryService,
    private remittanceSummaryService: RemittanceSummaryService,
    private createRemittanceService: CreateRemittanceService,
    private remittancesStageService: RemittanceStageService,
    private formBuilder: FormBuilder,
  ) {}

  ngOnInit() {
    this.globalFilterService.clientDataSourceSelectionChanged.subscribe(
      (selection) => {
        this.onClientSelectionChanged(selection);
      },
    );

    this.createSubscriptions()

    //  If we were notified by the Deposit Search then we will have
    //  the Check Detail in the payload.
    if (this.route.snapshot && this.route.snapshot.params) {
      this._depositSearch = new DepositSearchResult(
        this.route.snapshot.params['Check_Ref_Number'],
        this.route.snapshot.params['Payer_ShardPayerIdentifier'],
        this.route.snapshot.params['Check_CheckNumber'],
        this.route.snapshot.params['Check_CheckAmount'],
        this.route.snapshot.params['CheckStatus'],
        this.route.snapshot.params['Deposit_DepositDate'],
      );

      //  Notify subscribers of the (new) Check Detail record.
      this.remittanceEntryService.notifyCheckDetail(this.depositSearch);
    }

    if (this.globalFilterService.selectedEnvironment) {
      this._clientId = this.globalFilterService.selectedEnvironment.ClientId4Digit
    } else {
      return
    }

    //  Get the Remittance Stage data.
    this.loadStagedRemittances();

    //  Get the Remittance Summary data.
    this.loadRemittanceSummary();

    //  Initialize the controls.
    this.initForm();
  }

  ngOnDestroy(): void {
    this.onCreateRemittance.unsubscribe();
    this.onEditRemittance.unsubscribe();
    this.onUpdateRemittance.unsubscribe();
    this.onDeleteRemittance.unsubscribe();
  }

  private createSubscriptions(): void {
    this.onCreateRemittance = this.createRemittanceService.createRemittanceSource$.subscribe((remittance: RemittanceEntry) => {
      this.remittanceEntryService.notifyCreateRemittance(remittance);
      this.loadRemittanceSummary();
    })

    this.onEditRemittance = this.remittancesStageService.editRemittanceSource$.subscribe((remittance: RemittanceEntry) => {
      this.remittanceEntryService.notifyRemittanceStageEdit(remittance);
    })

    this.onUpdateRemittance = this.createRemittanceService.updateRemittanceSource$.subscribe((remittance: RemittanceEntry) => {
      this.remittanceEntryService.notifyUpdateRemittance(remittance);
      this.loadRemittanceSummary();
    })
    this.onDeleteRemittance = this.remittancesStageService.deleteRemittanceSource$.subscribe((remittance: RemittanceEntry) => {
      this.remittanceEntryService.notifyRemittanceStageDelete(remittance);
      this.loadRemittanceSummary();
    })
  }

  private initForm() {
    this.remittanceEntryForm = this.formBuilder.group({
      checkDetail: new FormControl(null),
      remittanceSummary: new FormControl(null),
      createRemittance: new FormControl(null),
      remittanceStage: new FormControl(null),
    });
  }

  private async loadStagedRemittances() {
    this.callNotifyService()
    this.formLoading = true;

    await this.remittancesStageService.loadStagedRemittances(
      this._clientId,
      this._depositSearch.Check_Ref_Number,
      this._filterCriteria)
      .then((data: ILimitedResult<RemittanceEntry>) => {
        this._remittanceEntries = data.Items
        this._totalItemCount = data.TotalItemCount
      })
      .catch( () => {
        toastr.error('An error occurred while loading Staged Remittances. If this problem continues please contact your account team.');
        this.formLoading = false;
      });
  }

  private callNotifyService() {
    if (
      !this.authorizationService.HealthcareTopBar ||
      !this.authorizationService.HealthcareTopBar.notifyActivity
    ) {
      return;
    }
    this.authorizationService.HealthcareTopBar.notifyActivity();
  }

  onClientSelectionChanged(selection: PrismDataSource): void {
    if (this.globalFilterService.selectedEnvironment) {
      this.router.navigateByUrl(DEPOSIT_SEARCH_ROUTE);
    } else {
      return;
    }
  }

  private async loadRemittanceSummary() {
    this.callNotifyService()
    await this.remittanceSummaryService.getRemittanceSummary(this.globalFilterService.selectedEnvironment.ClientId4Digit, this.depositSearch.Check_Ref_Number).then((summary) => {
      if (!summary) {
        summary = new RemittanceSummary();
        summary.DepositAmount = this.depositSearch.Check_CheckAmount;
        summary.Variance = this.depositSearch.Check_CheckAmount;
      }
      this.remittanceEntryService.notifyRemittanceSummary(summary);
    });
  }

}



// WEBPACK FOOTER //
// ./src/app/components/remittance-entry/remittance-entry.component.ts